import React, { useState, useEffect } from "react";
import { Box, Grid, TextField, Typography, Button, Chip, Switch, FormControlLabel, Divider, Alert, CircularProgress, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import { Globe, Save } from "lucide-react";
import { useTranslation } from "react-i18next";
const LANGUAGES = [{
  value: "ar",
  label: "العربية (Arabic)"
}, {
  value: "en",
  label: "English"
}, {
  value: "fr",
  label: "Français (French)"
}, {
  value: "tr",
  label: "Türkçe (Turkish)"
}, {
  value: "ur",
  label: "اردو (Urdu)"
}];
const GeneralTab = ({
  settings,
  save,
  saving,
  onTogglePublish
}) => {
  const {
    t
  } = useTranslation("website");
  const [form, setForm] = useState({});
  useEffect(() => {
    if (settings) setForm({
      siteName: settings.siteName || "",
      tagline: settings.tagline || "",
      slug: settings.slug || "",
      language: settings.language || "ar",
      direction: settings.direction || "rtl",
      metaDescription: settings.metaDescription || "",
      googleAnalyticsId: settings.googleAnalyticsId || "",
      footerText: settings.footerText || "",
      announcementBar: settings.announcementBar || {
        enabled: false,
        text: "",
        color: "#EF6C00"
      }
    });
  }, [settings]);
  const set = (key, val) => setForm(f => ({
    ...f,
    [key]: val
  }));
  const setAnnouncement = (key, val) => setForm(f => ({
    ...f,
    announcementBar: {
      ...f.announcementBar,
      [key]: val
    }
  }));
  const handleLanguageChange = lang => {
    const rtlLangs = ["ar", "ur", "he", "fa"];
    set("language", lang);
    set("direction", rtlLangs.includes(lang) ? "rtl" : "ltr");
  };
  const FRONTEND_BASE = (process.env.REACT_APP_URL || window.location.origin).replace(/\/$/, "");
  const siteUrl = form.slug ? `${FRONTEND_BASE}/w/${form.slug}` : "";
  return <Box>
      {/* Publish toggle */}
      <Box sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      mb: 3,
      p: 2,
      bgcolor: settings.isPublished ? "success.50" : "warning.50",
      borderRadius: 2,
      border: 1,
      borderColor: settings.isPublished ? "success.200" : "warning.200"
    }}>
        <Box>
          <Typography fontWeight={600}>
            {settings.isPublished ? t("Website is Live") : t("Website is Offline")}
          </Typography>
          {siteUrl && <Typography variant="caption" color="text.secondary">
              {siteUrl}
            </Typography>}
        </Box>
        <Box sx={{
        display: "flex",
        alignItems: "center",
        gap: 1
      }}>
          <Chip label={settings.isPublished ? "Published" : "Draft"} color={settings.isPublished ? "success" : "default"} size="small" />
          
          <Button variant={settings.isPublished ? "outlined" : "contained"} color={settings.isPublished ? "warning" : "success"} size="small" onClick={onTogglePublish} disabled={saving} startIcon={saving ? <CircularProgress size={14} /> : <Globe size={14} />}>
            
            {settings.isPublished ? t("Take Offline") : t("Publish")}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField label={t("Site Name")} value={form.siteName || ""} onChange={e => set("siteName", e.target.value)} fullWidth placeholder={t("My Store")} />
          
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label={t("URL Slug")} value={form.slug || ""} onChange={e => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} fullWidth placeholder={t("my-store")} helperText={siteUrl || "Letters, numbers, and hyphens only"} />
          
        </Grid>
        <Grid item xs={12}>
          <TextField label={t("Tagline")} value={form.tagline || ""} onChange={e => set("tagline", e.target.value)} fullWidth placeholder={t("Your one-stop shop for everything")} />
          
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>{t("Site Language")}</InputLabel>
            <Select value={form.language || "ar"} onChange={e => handleLanguageChange(e.target.value)} label={t("Site Language")}>
              {LANGUAGES.map(l => <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>{t("Text Direction")}</InputLabel>
            <Select value={form.direction || "rtl"} onChange={e => set("direction", e.target.value)} label={t("Text Direction")}>
              <MenuItem value="rtl">{t("Right to Left (RTL)")}</MenuItem>
              <MenuItem value="ltr">{t("Left to Right (LTR)")}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField label={t("Meta Description (SEO)")} value={form.metaDescription || ""} onChange={e => set("metaDescription", e.target.value)} fullWidth multiline rows={2} placeholder={t("Brief description of your site for search engines")} inputProps={{
          maxLength: 160
        }} helperText={`${(form.metaDescription || "").length}/160`} />
          
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label={t("Google Analytics ID")} value={form.googleAnalyticsId || ""} onChange={e => set("googleAnalyticsId", e.target.value)} fullWidth placeholder={t("G-XXXXXXXXXX")} />
          
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label={t("Footer Text")} value={form.footerText || ""} onChange={e => set("footerText", e.target.value)} fullWidth placeholder={t("© 2026 My Store. All rights reserved.")} />
          
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{
          my: 1
        }} />
          <Typography variant="subtitle2" gutterBottom>{t("Announcement Bar")}</Typography>
          <FormControlLabel control={<Switch checked={!!form.announcementBar?.enabled} onChange={e => setAnnouncement("enabled", e.target.checked)} />} label={t("Show announcement bar")} />
          
        </Grid>
        {form.announcementBar?.enabled && <>
            <Grid item xs={12} sm={8}>
              <TextField label={t("Announcement Text")} value={form.announcementBar?.text || ""} onChange={e => setAnnouncement("text", e.target.value)} fullWidth placeholder={t("Free shipping on orders over 200 SAR!")} />
            
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label={t("Bar Color")} type="color" value={form.announcementBar?.color || "#EF6C00"} onChange={e => setAnnouncement("color", e.target.value)} fullWidth />
            
            </Grid>
          </>}

        <Grid item xs={12}>
          <Button variant="contained" onClick={() => save(form)} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : <Save size={16} />}>{t("Save General Settings")}


          </Button>
        </Grid>
      </Grid>
    </Box>;
};
export default GeneralTab;
